import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, type QueryConstraint } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { COLLECTION_ORGANIZATIONS } from '../lib/firestorePaths';

/**
 * Subscribes to an org-scoped subcollection. Returns `{ items, loading }`;
 * items get their doc id injected as `id`.
 */
export function useOrgCollection<T extends { id: string }>(
  orgId: string | null,
  subcollection: string,
  ...constraints: QueryConstraint[]
): { items: T[]; loading: boolean } {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const ref = collection(db, COLLECTION_ORGANIZATIONS, orgId, subcollection);
    const unsubscribe = onSnapshot(
      constraints.length > 0 ? query(ref, ...constraints) : ref,
      (snap) => {
        setItems(snap.docs.map((d) => ({ ...(d.data() as T), id: d.id })));
        setLoading(false);
      },
      (error) => {
        console.error(`Failed to load ${subcollection}:`, error);
        setItems([]);
        setLoading(false);
      }
    );
    return unsubscribe;
    // constraints are intentionally not deps; callers pass stable or no constraints.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, subcollection]);

  return { items, loading };
}
